import { DEFAULT_NORMALIZE_BY_FIELD } from "constants/chart.constants";
import { EvaChartFeatures } from "types";

type NormalizationUnits = Record<string, string>;

/**
 * Well_Design.HzLength_m is the only normalization field with a real multi-unit (m/ft)
 * dictionary; a missing field (normalizeBy never set) falls back to it as the platform
 * default normalization field. An empty-string field is treated as "not the length field"
 * (not "missing") to match the unit/defaultUnit ternaries in translateOldChartSettingsToNewChartSettings,
 * which only special-case strict equality to the field name.
 */
export function isDefaultLengthNormalizationField(field: Maybe<string>): boolean {
  return field === undefined || field === null || field === DEFAULT_NORMALIZE_BY_FIELD;
}

/**
 * sanitizeObject (inside createMergeWithDefaults) silently drops explicit `null` overrides
 * for object-typed keys, so deepMerge inside createFeatureEntities always backfills
 * DEFAULT_NORMALIZATION_UNITS (m/ft) when a normalization field has no real units
 * dictionary of its own. Use this to compute the correct value to persist — a real
 * dictionary if one exists, otherwise the explicit null sentinel — from any raw source
 * (session settings, DB-saved settings, global normalizeBy settings).
 */
export function resolveExplicitNormalizationUnits(
  rawUnits: Maybe<NormalizationUnits>
): NormalizationUnits | null {
  return rawUnits ?? null;
}

/**
 * Restores the null units sentinel after a createFeatureEntities/deepMerge pass has
 * already run and (per the sanitizeObject caveat above) silently replaced it with
 * DEFAULT_NORMALIZATION_UNITS. Pass the same source units value that produced (or should
 * have produced) the sentinel — `null` forces it back, anything else (a real dictionary,
 * or `undefined` when there's no source data at all) is left untouched.
 */
export function restoreNormalizationUnitsSentinel(
  features: EvaChartFeatures,
  sourceUnits: Maybe<NormalizationUnits> | null
) {
  // features.normalization is absent for chart types with no normalization feature
  // (the midstream family) — optional-chain the write so this stays a no-op for them.
  if (sourceUnits === null && features.normalization?.properties) {
    features.normalization.properties.units = null;
  }
}
